import styles from './CourseProgressBar.module.css';
import type {RootState} from "../../../store/store";
import {useSelector} from 'react-redux';

/**
 * Props:
 *  - current      {number}  current lesson index (1-based)
 *  - total        {number}  total lessons
 *  - chapterTitle {string}  current chapter name
 *  - lessonTitle  {string}  current lesson name
 *  - onPrev       {func}    called when Prev is clicked
 *  - onNext       {func}    called when Next is clicked
 *  - hasPrev      {bool}
 *  - hasNext      {bool}
 */
export default function CourseProgressBar({
                                              current,
                                              total,
                                              percent,
                                              completed,
                                              chapterTitle,
                                              lessonTitle,
                                              onPrev,
                                              onNext,
                                              hasPrev,
                                              hasNext,
                                          }) {
    const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);

    return (
        <header className={styles.bar}>
            <div className={styles.inner}>

                {/* Left: breadcrumb */}
                <div className={styles.breadcrumb}>
                    <span className={styles.chapterLabel}>{chapterTitle}</span>
                    <span className={styles.separator}>/</span>
                    <span className={styles.lessonLabel}>{lessonTitle}</span>
                </div>

                {/* Centre: progress track */}
                <div className={styles.progressWrap}>
                    {isLoggedIn &&
                        <>
                            <div className={styles.track}>
                                <div className={styles.fill} style={{width: `${percent}%`}}/>
                            </div>
                            <span className={styles.progressText}>
                                {completed} <span className={styles.progressOf}>of</span> {total} lessons completed
                            </span>
                        </>
                    }
                </div>

                {/* Right: nav buttons */}
                <div className={styles.nav}>
                    <button
                        className={styles.navBtn}
                        onClick={onPrev}
                        disabled={!hasPrev}
                        aria-label="Previous lesson"
                    >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M8.5 2.5L4.5 7L8.5 11.5" stroke="currentColor" strokeWidth="1.6"
                                  strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Prev
                    </button>
                    <button
                        className={`${styles.navBtn} ${styles.navBtnPrimary}`}
                        onClick={onNext}
                        disabled={!hasNext}
                        aria-label="Next lesson"
                    >
                        Next
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M5.5 2.5L9.5 7L5.5 11.5" stroke="currentColor" strokeWidth="1.6"
                                  strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>

            </div>

            {/* Full-width progress line at the very bottom of the bar */}
            <div className={styles.bottomTrack}>
                <div className={styles.bottomFill} style={{width: `${percent}%`}}/>
            </div>
        </header>
    );
}
